import React, { useState, useEffect, useMemo } from 'react';
import './GameCreationMagic.css';

interface GameCreationMagicProps {
  onComplete: () => void;
  gameCreationPromise: Promise<any>;
  playerName: string;
}

const GameCreationMagic: React.FC<GameCreationMagicProps> = ({
  onComplete,
  gameCreationPromise,
  playerName
}) => {
  const [stage, setStage] = useState(0);
  const [isGameReady, setIsGameReady] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Этапы магии (9 секунд - больше времени на чтение)
  const magicStages = [
    { 
      text: "72 клетки пути от мира форм к большему осознанию…", 
      duration: 3500, // Увеличено с 2500 до 3500
      animation: "unfold-field"
    },
    { 
      text: "Точка старта и финиш — 68: Космическое сознание.", 
      duration: 3000, // Увеличено с 1500 до 3000
      animation: "highlight-start-finish"
    },
    { 
      text: "Твой личный проводник готов к диалогу", 
      duration: 2500, // Увеличено с 1500 до 2500
      animation: "summon-guide"
    }
  ];

  // Состояния ожидания
  const waitingStages = [
    "Готовим твоё путешествие...",
    "Настраиваем связь с проводником...",
    "Почти готово..."
  ];

  // Отслеживание готовности игры
  useEffect(() => {
    const magicTimer = setTimeout(() => {
      setShowWaiting(true);
    }, 9000); // 9 секунд (общее время магии)

    gameCreationPromise.then(() => {
      setIsGameReady(true);
      if (stage >= magicStages.length) {
        // Если магия уже закончилась, завершаем сразу
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(onComplete, 300);
        }, 500);
      }
    });

    return () => clearTimeout(magicTimer);
  }, [gameCreationPromise, onComplete, stage, isGameReady, magicStages.length]);

  // Переход между этапами магии
  useEffect(() => {
    if (stage < magicStages.length) {
      const timer = setTimeout(() => {
        setStage(prev => prev + 1);
      }, magicStages[stage].duration);

      return () => clearTimeout(timer);
    } else if (isGameReady) {
      // Магия закончилась и игра готова - ждем еще немного перед завершением
      setTimeout(() => {
        setIsVisible(false);
        setTimeout(onComplete, 300);
      }, 1000); // Увеличиваем время показа последнего этапа
    }
  }, [stage, isGameReady, onComplete, magicStages]);

  // Если игра готова раньше - ускоряем завершение магии
  useEffect(() => {
    if (isGameReady && stage >= magicStages.length) {
      setTimeout(() => {
        setIsVisible(false);
        setTimeout(onComplete, 300);
      }, 1000); // Увеличиваем время показа последнего этапа
    }
  }, [isGameReady, stage, magicStages.length, onComplete]);

  // Переход между этапами ожидания
  useEffect(() => {
    if (showWaiting && !isGameReady) {
      let waitingStage = 0;
      const waitingTimer = setInterval(() => {
        waitingStage = (waitingStage + 1) % waitingStages.length;
        // Обновляем текст ожидания (можно добавить состояние для этого)
      }, 2000);

      return () => clearInterval(waitingTimer);
    }
  }, [showWaiting, isGameReady]);

  // 🚀 ОПТИМИЗАЦИЯ: Создаем поле ОДИН РАЗ с помощью useMemo - больше никаких перерендеров!
  const gameField = useMemo(() => {
    console.log('🚀 Создаем игровое поле ОДИН РАЗ!');
    
    const result = [];
    
    // ТОЧНО ТАКАЯ ЖЕ ЛОГИКА КАК В РЕАЛЬНОМ GameBoard.tsx!
    // Рендерим сверху вниз (row 7 -> 0)
    for (let row = 7; row >= 0; row--) {
      const baseStart = row * 9 + 1;
      
      if (row % 2 === 0) {
        // Четные ряды: слева направо (1-9, 17-25, ...)
        for (let col = 0; col < 9; col++) {
          const cellNumber = baseStart + col;
          result.push(cellNumber);
        }
      } else {
        // Нечетные ряды: справа налево (18-10, 26-34, ...)
        for (let col = 8; col >= 0; col--) {
          const cellNumber = baseStart + col;
          result.push(cellNumber);
        }
      }
    }
    
    return result.map((cellNumber, i) => {
      
      let specialClass = '';
      
      // Клетка 68 - Старт/Финиш
      if (cellNumber === 68) {
        specialClass = 'start-finish-cell';
        return (
          <div 
            key={cellNumber} 
            className={`magic-cell ${specialClass}`}
            style={{ animationDelay: `${i * 20}ms` }}
          >
            <div className="cell-number">68</div>
          </div>
        );
      }
      // Клетки 69, 70, 71 - со звездочками
      else if ([69, 70, 71].includes(cellNumber)) {
        specialClass = 'star-cell';
        return (
          <div 
            key={cellNumber} 
            className={`magic-cell ${specialClass}`}
            style={{ animationDelay: `${i * 20}ms` }}
          >
            <div className="cell-number">{cellNumber}</div>
            <div className="star-icon">⭐</div>
          </div>
        );
      }
      // Змеи
      else if ([72, 63, 62, 55, 52, 44, 29, 24, 16, 12].includes(cellNumber)) {
        specialClass = 'snake-cell';
        return (
          <div 
            key={cellNumber} 
            className={`magic-cell ${specialClass}`}
            style={{ animationDelay: `${i * 20}ms` }}
          >
            <div className="cell-number">{cellNumber}</div>
            <div className="snake-icon">🐍</div>
          </div>
        );
      }
      // Лестницы
      else if ([17, 22, 10, 27, 20, 37, 54, 46, 45, 28].includes(cellNumber)) {
        specialClass = 'ladder-cell';
        return (
          <div 
            key={cellNumber} 
            className={`magic-cell ${specialClass}`}
            style={{ animationDelay: `${i * 20}ms` }}
          >
            <div className="cell-number">{cellNumber}</div>
            <div className="ladder-icon">🪜</div>
          </div>
        );
      }
      // Обычные клетки
      else {
        return (
          <div 
            key={cellNumber} 
            className="magic-cell"
            style={{ animationDelay: `${i * 20}ms` }}
          >
            <div className="cell-number">{cellNumber}</div>
          </div>
        );
      }
    });
  }, []); // Пустой массив зависимостей - создаем только один раз!

  if (!isVisible) return null;

  const currentStage = magicStages[stage];
  const isWaiting = showWaiting && !isGameReady;
  const currentText = isWaiting ? waitingStages[0] : currentStage?.text;

  // Отладочная информация
  console.log('Magic stage:', stage, 'animation:', currentStage?.animation, 'isVisible:', isVisible);
  console.log('Gameboard classes:', `magic-gameboard ${currentStage?.animation || ''} ${stage === 1 || stage === 2 ? 'highlight-cell-68' : ''}`);

  return (
    <div className="game-creation-magic">
      <div className="magic-overlay">
        {/* Заголовок */}
        <div className="magic-title">
          Создание вашей игры
        </div>
        
        {/* Анимированное игровое поле */}
        <div className={`magic-gameboard ${currentStage?.animation || ''} ${stage === 1 || stage === 2 ? 'highlight-cell-68' : ''}`}>
          <div className="magic-cells">
            {gameField}
          </div>
        </div>

        {/* Текст магии */}
        <div className="magic-text-container">
          <div className="magic-text">
            {currentText}
          </div>
          
          {/* Прогресс бар */}
          <div className="magic-progress-bar">
            <div 
              className="magic-progress-fill" 
              style={{ 
                width: isWaiting ? '100%' : `${((stage + 1) / magicStages.length) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Индикатор ожидания */}
        {isWaiting && (
          <div className="magic-waiting">
            <div className="magic-spinner"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCreationMagic;